<template>
  <div>
    <!-- Bu div, tüm bileşeni saracak -->
    <component :is="resolveLayoutVariant" :class="`skin-variant--${appSkinVariant}`">
      <transition :name="appRouteTransition" mode="out-in" appear>
        <router-view></router-view>
      </transition>
    </component>

    <v-dialog
      v-model="appAlertShow"
      max-width="380"
    >
      <v-card class="app-alert-dialog rounded-xl">
        <v-btn
          icon
          small
          class="app-alert-dialog__close"
          @click="hideAppAlert"
        >
          <v-icon size="20">
            {{ icons.mdiClose }}
          </v-icon>
        </v-btn>

        <v-card-text class="text-center px-6 pt-7 pb-1">
          <v-avatar
            :color="appAlertAvatarColor"
            size="52"
            class="mb-3"
          >
            <v-icon color="white" size="26">
              {{ appAlertIcon }}
            </v-icon>
          </v-avatar>
          <p class="text-subtitle-1 font-weight-semibold primary--text mb-2">
            {{ appAlertTitle }}
          </p>
          <p class="text-body-2 mb-0 app-alert-dialog__message">
            {{ appAlertMessage }}
          </p>
        </v-card-text>

        <v-card-actions class="px-6 pb-5 pt-3 justify-center">
          <v-btn
            color="primary"
            depressed
            rounded
            min-width="110"
            class="text-capitalize px-5"
            @click="hideAppAlert"
          >
            Tamam
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { computed } from '@vue/composition-api'
// eslint-disable-next-line import/no-unresolved
import useAppConfig from '@core/@app-config/useAppConfig'
import { useLayout } from '@core/layouts/composable/useLayout'
import { useRouter } from '@core/utils'

// Layouts
import LayoutBlank from '@/layouts/variants/blank/LayoutBlank.vue'
import LayoutContentHorizontalNav from '@/layouts/variants/content/horizontal-nav/LayoutContentHorizontalNav.vue'
import LayoutContentVerticalNav from '@/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import store from '@/store'
import useDynamicVh from '@core/utils/useDynamicVh'
import { mdiAlert, mdiAlertCircle, mdiClose } from '@mdi/js'
import Swal from 'sweetalert2'
import themeConfig from '@themeConfig'
import { checkVersion, updateVersion } from '@/services/versionControl'

import { APP_VERSION } from './version'

export default {
  components: {
    LayoutContentVerticalNav,
    LayoutContentHorizontalNav,
    LayoutBlank,
  },
  setup() {
    const version_no = APP_VERSION
    const { route } = useRouter()
    const { appContentLayoutNav, appSkinVariant, appRouteTransition } = useAppConfig()

    const { handleBreakpointLayoutSwitch } = useLayout()
    handleBreakpointLayoutSwitch()

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === 'blank') return 'layout-blank'
      if (route.value.meta.layout === 'content') return `layout-content-${appContentLayoutNav.value}-nav`

      return null
    })

    useDynamicVh()

    const appAlertShow = computed({
      get: () => store.state.appAlert.show,
      set: value => {
        if (!value) {
          store.commit('HIDE_APP_ALERT')
        }
      },
    })
    const appAlertTitle = computed(() => store.state.appAlert.title)
    const appAlertMessage = computed(() => store.state.appAlert.message)
    const appAlertType = computed(() => store.state.appAlert.type)
    const appAlertIcon = computed(() => (
      appAlertType.value === 'error' ? mdiAlertCircle : mdiAlert
    ))
    const appAlertAvatarColor = computed(() => (
      appAlertType.value === 'error' ? 'error' : 'warning'
    ))

    const hideAppAlert = () => {
      store.commit('HIDE_APP_ALERT')
    }

    return {
      resolveLayoutVariant,
      appSkinVariant,
      appRouteTransition,
      version_no,
      appAlertShow,
      appAlertTitle,
      appAlertMessage,
      appAlertType,
      appAlertIcon,
      appAlertAvatarColor,
      hideAppAlert,
      icons: {
        mdiClose,
      },
    }
  },
  mounted() {
    this.checkForUpdates()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkForUpdates()
      }
    },
    async checkForUpdates() {
      const result = await checkVersion()
      if (result.hasUpdate && result.currentVersion !== result.latestVersion) {
        Swal.fire({
          title: 'Güncelleme Mevcut!',
          text: 'Yeni bir güncelleme var. Sayfayı yenilemek ister misiniz?',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Evet, Yenile',
          cancelButtonText: 'Hayır',
          confirmButtonColor: themeConfig.themes.light.primary,
          cancelButtonColor: themeConfig.themes.light.secondary,
        }).then(res => {
          updateVersion(result.latestVersion)
          if (res.isConfirmed) {
            window.location.reload()
          }
        })
      }
    },
  },
}
</script>

<style lang="scss">
// SweetAlert2 penceresi <body>'ye eklenir; Vuetify fontu yalnızca .v-application içinde tanımlı olduğundan
// serif'e (Times) düşüyordu. Uygulama fontu ($body-font-family) burada verilir.
.swal2-container {
  font-family: 'Inter', sans-serif;
}

// Uygulamadaki diğer uyarı penceresiyle (.app-alert-dialog: rounded-xl, hap butonlar) aynı görünüm
.swal2-popup {
  width: 26rem;
  max-width: calc(100% - 2rem);
  border-radius: 24px;
}

// Bilgi ikonu da butonlarla aynı marka mavisi
.swal2-icon.swal2-info {
  border-color: #368ee0;
  color: #368ee0;
}

.swal2-title {
  font-size: 1.25rem;
  font-weight: 600;
}

.swal2-html-container {
  font-size: 0.875rem;
}

.swal2-styled.swal2-confirm,
.swal2-styled.swal2-cancel {
  border-radius: 999px;
  padding: 0.6em 1.6em;
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff !important;
}

.app-alert-dialog {
  position: relative;

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
  }

  &__message {
    color: rgba(94, 86, 105, 0.85);
    line-height: 1.6;
    white-space: pre-line;
  }
}
</style>
